import React from 'react';
import Header from '@/components/ui/custom/table/Header';
import Text from '@/components/ui/custom/dataDisplay/Text';
import DataTeachersTable from './DataTeachersTable';
import SearchAndFilterButtons from '@/components/ui/custom/SearchAndFilterButtons';
import { Teacher } from '@/models/Klass';
import { TeacherHeader } from '../../../utils/HeaderTypes';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { LoadingIcon } from '../../icons';
import useFilterParams from '../../../utils/UseFilterParams';
import { ListUsersFilters } from '@/data/services/userServices';
import { QuickFilterProps as QuickFilterItem } from '@/components/ui/custom/filter/QuickFilter';
import QuickFilters from '@/components/ui/custom/filter/QuickFilters';
import { useNavigate } from 'react-router-dom';

interface ListTeachersProps {
  teachersList: Teacher[];
  searchTeacher?: string;
  titles: TeacherHeader;
  isSearch: boolean;
  isLoading?: boolean;
}

export default function ListTeachers({
  teachersList,
  isSearch,
  searchTeacher,
  titles,
  isLoading,
}: ListTeachersProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'manageTeacher.dataUserTable',
  });

  const navigate = useNavigate();

  const { filterParams, setFilterParams, deleteFilterParams } =
    useFilterParams<ListUsersFilters>();

  const search = filterParams.get('search') ?? '';

  const initialSearch = searchTeacher?.replaceAll('_', ' ') ?? search;

  const hasSearch = !!search || isSearch;

  const hasParams = [...filterParams.keys()].length !== 0;

  const requestSearch = (value: string) => {
    setFilterParams({ pageNumber: 1, search: value || undefined });
  };

  function clearSearch() {
    deleteFilterParams('search');
    navigate('/admin/teachers');
  }

  function clearFilters() {
    deleteFilterParams(['search', 'unitId']);
    navigate('/admin/teachers');
  }

  function clearSorts() {
    setFilterParams({ pageNumber: 1, ordering: undefined });
  }

  function clearFiltersAndSorts() {
    clearFilters();
    clearSorts();
  }

  const quickFilters: QuickFilterItem[] = [
    {
      type: 'unitId',
      filters: filterParams.getAll('unitId'),
    },
    {
      type: 'staffStatus',
      filters: filterParams.getAll('status'),
    },
  ];

  return (
    <React.Fragment>
      <div className="flex gap-4 justify-between items-center">
        <SearchAndFilterButtons
          searchInitial={initialSearch}
          onChange={debounce(e => requestSearch(e.target.value), 500)}
          isSearch={hasSearch}
          clearFiltersButton={clearFilters}
          clearOrderButton={clearSorts}
          clearAllButton={clearFiltersAndSorts}
          clearSearch={clearSearch}
        />
        <QuickFilters quickFilters={quickFilters} />
      </div>

      <div>
        <table className="w-full flex flex-col gap-y-2.5">
          <Header headers={Object.values(titles)} />
          <ConditionalRenderer condition={!isLoading}>
            <DataTeachersTable titles={titles} teachersList={teachersList} />
          </ConditionalRenderer>
        </table>
        <ConditionalRenderer condition={!teachersList.length && !isLoading}>
          <Text
            text={hasParams ? t('searchNotFound') : t('noTeachers')}
            className="flex justify-center mt-5"
          />
        </ConditionalRenderer>
        <ConditionalRenderer condition={isLoading}>
          <div className="flex w-full h-[518px] justify-center items-center p-3.5">
            <LoadingIcon className="text-primary/40 w-10 h-10" />
          </div>
        </ConditionalRenderer>
      </div>
    </React.Fragment>
  );
}
